import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import {
  downloadAsCSV,
  downloadAsPDF,
  downloadReceiverEnvelope,
  downloadSenderEnvelope,
} from 'services/repositories/transactions/transactions';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { useOCRAvailability } from 'support/helpers/ocr/ocr';
import { DocumentType, DocumentTypeWithVirtual, TransactionOriginApp, ValueOf } from 'support/types';

import { useConfirmOrderAction } from './actions/confirmOrderAction';
import { useDeliverTestMessageAction } from './actions/deliverTestMessageAction';
import { useEditAndReprocessAction } from './actions/editAndReprocessAction';
import { useForceDocumentProcessingAction } from './actions/forceDocumentProcessingAction';
import { useDocumentActionWithOCR } from './actions/helpers';
import { useRejectOrderAction } from './actions/rejectOrderAction';
import { useReprocessMessageAction } from './actions/reprocessMessageAction';
import { useResendAsReceiverAction } from './actions/resendAsReceiverAction';
import { DestructiveAction } from './components/DestructiveAction/DestructiveAction';
import { PrimaryAction } from './components/PrimaryAction/PrimaryAction';
import { SecondaryActions, TestSubmenuItem } from './components/SecondaryAction/SecondaryActions';
import { ActionConfig, DocumentTypeActionConfig, MessageV2DTO } from './components/types';

type ActionButtonsProps = {
  message: MessageV2DTO;
  messageType: ValueOf<DocumentTypeWithVirtual>;
  messageOriginApp: TransactionOriginApp;
  canDownloadOriginalFile: boolean;
  relatedTransactions: Array<MessageV2DTO> | undefined;
  enabledDocumentTypes: Array<DocumentType>;
  testingDocumentTypes: Array<DocumentType>;
  onReprocessingChange?: (isReprocessing: boolean) => void;
};

const TEST_SUBMENU_LABEL_BY_DOCUMENT_TYPE: Partial<Record<DocumentType, string>> = {
  [DocumentType.shippingNotice]: 'transactions:show.actionButtons.testSubmenu.shippingNotice',
  [DocumentType.invoice]: 'transactions:show.actionButtons.testSubmenu.invoice',
  [DocumentType.creditNote]: 'transactions:show.actionButtons.testSubmenu.creditNote',
  [DocumentType.dispatchResponse]: 'transactions:show.actionButtons.testSubmenu.dispatchResponse',
};

export function ActionButtons({
  message,
  relatedTransactions,
  enabledDocumentTypes,
  testingDocumentTypes,
  messageType,
  messageOriginApp,
  canDownloadOriginalFile,
  onReprocessingChange,
}: ActionButtonsProps) {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const destinationProcess = useWebEDIStore((state) => state.destinationProcess);
  const sourceProcess = useWebEDIStore((state) => state.sourceProcess);
  const sourceNormalizer = sourceProcess?.configuration?.normalizer;
  const destinationNormalizer = destinationProcess?.configuration?.normalizer;

  const orderResponseOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.orderResponse,
  });
  const shippingNoticeOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.shippingNotice,
  });
  const invoiceOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.invoice,
  });
  const creditNoteOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.creditNote,
  });
  const dispatchResponseOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.dispatchResponse,
  });

  const createOrderResponseAction: ActionConfig = useConfirmOrderAction(
    message,
    DocumentType.orderResponse,
    relatedTransactions,
    orderResponseOCRAvailability,
    {
      label: t('transactions:show.actionButtons.createOrderResponse'),
      analyticsId: 'transactions:confirm_order',
    },
  );

  const rejectOrderAction: ActionConfig = useRejectOrderAction(
    message,
    DocumentType.orderResponse,
    relatedTransactions,
    createOrderResponseAction.test,
  );

  const forceDocumentProcessingAction: ActionConfig = useForceDocumentProcessingAction(message);

  const { action: reprocessMessageAction, isReprocessing } = useReprocessMessageAction(message);
  const resendAsReceiverAction: ActionConfig = useResendAsReceiverAction(message);
  const { action: deliverTestMessageAction } = useDeliverTestMessageAction(message);
  const editAndReprocessAction: ActionConfig = useEditAndReprocessAction(enabledDocumentTypes);

  useEffect(() => {
    onReprocessingChange?.(isReprocessing);
  }, [isReprocessing, onReprocessingChange]);

  const createShippingNoticeAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.shippingNotice,
    relatedTransactions,
    shippingNoticeOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createShippingNotice',
      analyticsId: 'transactions:create_shipping_notice',
    },
  );

  const createInvoiceAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.invoice,
    relatedTransactions,
    invoiceOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createInvoice',
      analyticsId: 'transactions:create_invoice',
    },
  );

  const createCreditNoteAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.creditNote,
    relatedTransactions,
    creditNoteOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createCreditNote',
      analyticsId: 'transactions:create_credit_note',
    },
  );

  const createDispatchResponseAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.dispatchResponse,
    relatedTransactions,
    dispatchResponseOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createDispatchResponse',
      analyticsId: 'transactions:create_dispatch_response',
    },
  );

  const downloadAsCSVAction: ActionConfig = {
    label: 'transactions:show.actionButtons.downloadAsCSV',
    secondaryOptionOnly: true,
    group: 'download',
    analyticsId: 'transactions:download_as_csv',
    onClick: () => downloadAsCSV(message),
  };

  const downloadAsPDFAction: ActionConfig = {
    label: 'transactions:show.actionButtons.downloadAsPDF',
    secondaryOptionOnly: true,
    group: 'download',
    analyticsId: 'transactions:download_as_pdf',
    onClick: () => downloadAsPDF(message),
    test: () => {
      const result = 'UNATTENDED_OCR' !== messageOriginApp && 'PORTAL_OCR' !== messageOriginApp;
      return result;
    },
  };

  const downloadAsSenderEnvelopeRawPayload: ActionConfig = {
    label:
      'transactions:show.actionButtons.downloadSenderEnvelopeRawPayload' +
      (sourceNormalizer ? `_${sourceNormalizer}` : ''),
    onClick: (message: MessageV2DTO) => {
      if (!message.sender_envelope) {
        return;
      }

      return downloadSenderEnvelope(message.sender_envelope.id);
    },
    secondaryOptionOnly: true,
    group: 'download',
    analyticsId: 'transactions:download_sender_envelope_raw_payload',
    test: (message: MessageV2DTO) => {
      const { sender_envelope: senderEnvelope, sender_partner: senderPartner } = message;

      if (!senderEnvelope) {
        return false;
      }
      const isMessageContentIrrelevant = messageOriginApp != null;

      if (isMessageContentIrrelevant) {
        return false;
      }

      return senderPartner?.id === currentPartner?.id;
    },
  };

  const downloadOriginalFileAction: ActionConfig = {
    label: 'transactions:show.actionButtons.downloadOriginalFile',
    secondaryOptionOnly: true,
    group: 'download',
    analyticsId: 'transactions:download_original_file',
    onClick: (message: MessageV2DTO) => {
      if (!message.sender_envelope) {
        return;
      }

      return downloadSenderEnvelope(message.sender_envelope.id);
    },
    test: (message: MessageV2DTO) =>
      canDownloadOriginalFile && !!message.sender_envelope && message.receiver_partner?.id === currentPartner?.id,
  };

  const downloadAsReceiverEnvelopeRawPayload: ActionConfig = {
    label:
      'transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload' +
      (destinationNormalizer ? `_${destinationNormalizer}` : ''),
    onClick: (message: MessageV2DTO) => {
      if (!message.receiver_envelope) {
        return;
      }

      return downloadReceiverEnvelope(message.receiver_envelope.id);
    },
    secondaryOptionOnly: true,
    group: 'download',
    analyticsId: 'transactions:download_receiver_envelope_raw_payload',
    test: (message: MessageV2DTO) => {
      const {
        receiver_envelope: receiverEnvelope,
        receiver_partner: receiverPartner,
        sender_partner: senderPartner,
        type,
      } = message;

      if (!receiverEnvelope) {
        return false;
      }

      if (receiverPartner?.id === currentPartner?.id) {
        return true;
      }

      return (
        type === DocumentType.salesReport &&
        senderPartner?.id === currentPartner?.id &&
        destinationNormalizer === 'EXCEL'
      );
    },
  };

  const orderActions: DocumentTypeActionConfig = {
    documentType: DocumentType.order,
    actions: [
      createOrderResponseAction,
      rejectOrderAction,
      createShippingNoticeAction,
      createInvoiceAction,
      downloadAsPDFAction,
      downloadAsCSVAction,
    ],
  };

  const orderResponseActions: DocumentTypeActionConfig = {
    documentType: DocumentType.orderResponse,
    actions: [createShippingNoticeAction, createInvoiceAction, downloadAsPDFAction],
  };

  const shippingNoticeActions: DocumentTypeActionConfig = {
    documentType: DocumentType.shippingNotice,
    actions: [createInvoiceAction, downloadAsPDFAction],
  };

  const invoiceActions: DocumentTypeActionConfig = {
    documentType: DocumentType.invoice,
    actions: [createCreditNoteAction, downloadAsPDFAction],
  };

  const creditNoteActions: DocumentTypeActionConfig = {
    documentType: DocumentType.creditNote,
    actions: [downloadAsPDFAction],
  };

  const dispatchInstructionActions: DocumentTypeActionConfig = {
    documentType: DocumentType.dispatchInstruction,
    actions: [createDispatchResponseAction, downloadAsPDFAction],
  };

  const dispatchResponseActions: DocumentTypeActionConfig = {
    documentType: DocumentType.dispatchResponse,
    actions: [downloadAsPDFAction],
  };

  const documentActions: Array<DocumentTypeActionConfig> = [
    orderActions,
    orderResponseActions,
    shippingNoticeActions,
    invoiceActions,
    creditNoteActions,
    dispatchInstructionActions,
    dispatchResponseActions,
  ];

  const allDocumentsActions = [
    deliverTestMessageAction,
    downloadAsSenderEnvelopeRawPayload,
    downloadOriginalFileAction,
    downloadAsReceiverEnvelopeRawPayload,
    forceDocumentProcessingAction,
    reprocessMessageAction,
    resendAsReceiverAction,
    editAndReprocessAction,
  ];
  const actions: Array<ActionConfig> =
    documentActions
      .find(
        (action: DocumentTypeActionConfig) =>
          action.documentType === mapVirtualToActualDocumentType(messageType as DocumentTypeWithVirtual),
      )
      ?.actions?.concat(allDocumentsActions) ?? allDocumentsActions;

  const availableActions = actions
    .filter(
      (action: ActionConfig) =>
        typeof action.documentType === 'undefined' || enabledDocumentTypes.includes(action.documentType),
    )
    .filter((action: ActionConfig) => !action.test || action.test(message));

  const isTestAction = (action: ActionConfig) =>
    typeof action.documentType !== 'undefined' && testingDocumentTypes.includes(action.documentType);

  const liveActions: Array<ActionConfig> = [];
  const liveDestructiveActions: Array<ActionConfig> = [];
  const testActions: Array<ActionConfig> = [];
  const otherActions: Array<ActionConfig> = [];

  for (const action of availableActions) {
    if (typeof action.documentType === 'undefined') {
      otherActions.push(action);
      continue;
    }
    if (isTestAction(action)) {
      testActions.push(action);
      continue;
    }
    if (action.type === 'destructive') {
      liveDestructiveActions.push(action);
      continue;
    }
    liveActions.push(action);
  }

  const destructiveAction: ActionConfig | null = liveDestructiveActions[0] ?? null;
  const primaryAction: ActionConfig | null =
    liveActions.find((action) => !action.secondaryOptionOnly && !action.documentTypeExists) ?? null;

  const createGroup: Array<ActionConfig> = liveActions.filter((action) => action !== primaryAction);
  const downloadGroup: Array<ActionConfig> = otherActions.filter((a) => a.group === 'download');
  const processingGroup: Array<ActionConfig> = otherActions.filter((a) => a.group === 'processing');
  const resendGroup: Array<ActionConfig> = otherActions.filter((a) => a.group === 'resend');
  const testGroup: Array<TestSubmenuItem> = testActions
    .map(buildTestSubmenuItem)
    .filter((item): item is TestSubmenuItem => item !== null);

  const modalActions = [
    ...createGroup,
    ...downloadGroup,
    ...processingGroup,
    ...resendGroup,
    ...testActions,
    ...(primaryAction ? [primaryAction] : []),
    ...(destructiveAction ? [destructiveAction] : []),
  ];
  const renderedModalIds = new Set<string>();

  return (
    <div className="flex flex-row items-center">
      <SecondaryActions
        groups={{
          create: createGroup,
          download: downloadGroup,
          processing: processingGroup,
          test: testGroup,
          resend: resendGroup,
        }}
        fromDocument={message}
      />
      <DestructiveAction action={destructiveAction} fromDocument={message} />
      <PrimaryAction action={primaryAction} fromDocument={message} />

      {modalActions.map((action) => {
        if (!action.renderModal || renderedModalIds.has(action.analyticsId)) {
          return null;
        }
        renderedModalIds.add(action.analyticsId);
        return <span key={action.analyticsId}>{action.renderModal(message)}</span>;
      })}
    </div>
  );
}

const buildTestSubmenuItem = (action: ActionConfig): TestSubmenuItem | null => {
  if (!action.documentType) {
    return null;
  }

  if (action.documentType === DocumentType.orderResponse) {
    if (action.type === 'destructive') {
      return {
        kind: 'flat',
        label: 'transactions:show.actionButtons.testSubmenu.orderRejection',
        onClick: action.onClick,
        analyticsId: action.analyticsId,
        testId: action.testId,
      };
    }

    if (action.subActions?.length) {
      return {
        kind: 'nested',
        label: 'transactions:show.actionButtons.testSubmenu.orderConfirmation',
        analyticsId: action.analyticsId,
        subActions: action.subActions,
      };
    }

    return null;
  }

  const label = TEST_SUBMENU_LABEL_BY_DOCUMENT_TYPE[action.documentType];
  if (!label) {
    return null;
  }

  if (action.subActions?.length) {
    return {
      kind: 'nested',
      label,
      analyticsId: action.analyticsId,
      subActions: action.subActions,
    };
  }

  return {
    kind: 'flat',
    label,
    onClick: action.onClick,
    analyticsId: action.analyticsId,
    testId: action.testId,
  };
};

/**
 * i18n
 * t('transactions:show.actionButtons.createOrderResponse')
 * t('transactions:show.actionButtons.createShippingNotice')
 * t('transactions:show.actionButtons.createCreditNote')
 * t('transactions:show.actionButtons.createInvoice')
 * t('transactions:show.actionButtons.createDispatchResponse')
 *
 * t('transactions:show.actionButtons.sendTestDocument')
 * t('transactions:show.actionButtons.testSubmenu.orderConfirmation')
 * t('transactions:show.actionButtons.testSubmenu.orderRejection')
 * t('transactions:show.actionButtons.testSubmenu.shippingNotice')
 * t('transactions:show.actionButtons.testSubmenu.invoice')
 * t('transactions:show.actionButtons.testSubmenu.creditNote')
 * t('transactions:show.actionButtons.testSubmenu.dispatchResponse')
 *
 * t('transactions:show.actionButtons.downloadAsPDF')
 * t('transactions:show.actionButtons.downloadAsCSV')
 * t('transactions:show.actionButtons.resend')
 * t('transactions:show.actionButtons.editAndReprocess')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EDIFACT')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EDIFACT_V2')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_PHBEST')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_FIXED')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_TRADACOMS')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_XML')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EXCEL')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_HYBRID_PDF')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_CSV')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_NONE')
 *
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_EDIFACT')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_EDIFACT_V2')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_PHBEST')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_FIXED')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_TRADACOMS')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_XML')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_EXCEL')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_HYBRID_PDF')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_CSV')
 * t('transactions:show.actionButtons.downloadSenderEnvelopeRawPayload_NONE')
 * t('transactions:show.actionButtons.downloadOriginalFile')
 *
 */
